import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Badge from "../../../shared/Badge";
import Button from "../../../shared/Button";
import Input from "../../../shared/Input";
import Modal from "../../../shared/Modal";
import Spinner from "../../../shared/Spinner";
import TextArea from "../../../shared/TextArea";
import InvoiceSummary from "../components/InvoiceSummary";
import {
  useAddInvoiceV2PaymentMutation,
  useGetInvoiceV2Query,
  useRemindInvoiceV2Mutation,
  useSendInvoiceV2Mutation,
} from "../../../redux/services/invoices/invoicesV2Api";
import { renderAmount } from "../../../utils";
import {
  getInvoiceStatusColor,
  getInvoiceStatusLabel,
  toTaxRatePercent,
} from "../utils";
import { InvoiceTagsInline } from "../../../components";

function renderLineTax(lineItem = {}) {
  if (!lineItem.taxable) {
    return "No tax";
  }

  const explicitTaxAmount = Number(lineItem.taxAmount || 0);
  const calculatedTaxAmount =
    explicitTaxAmount > 0
      ? explicitTaxAmount
      : Number(lineItem.amount || 0) * Number(lineItem.taxRate || 0);

  return `${(Number(lineItem.taxRate || 0) * 100).toFixed(2)}% / ${renderAmount(
    calculatedTaxAmount
  )}`;
}

const InvoiceV2Show = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { data: invoice, isLoading, isError, error } = useGetInvoiceV2Query(
    { id: params.id },
    { skip: !params.id }
  );
  const [sendInvoiceV2, { isLoading: sending }] = useSendInvoiceV2Mutation();
  const [remindInvoiceV2, { isLoading: reminding }] = useRemindInvoiceV2Mutation();
  const [addInvoiceV2Payment, { isLoading: recordingPayment }] =
    useAddInvoiceV2PaymentMutation();

  const [sendModalVisible, setSendModalVisible] = useState(false);
  const [reminderModalVisible, setReminderModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [sendRecipients, setSendRecipients] = useState("");
  const [sendSubject, setSendSubject] = useState("");
  const [reminderRecipients, setReminderRecipients] = useState("");
  const [reminderSubject, setReminderSubject] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentNote, setPaymentNote] = useState("");

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 px-4 py-6 md:px-6">
        <Spinner size="md" />
        <span className="text-sm text-gray-600">Loading invoice...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4 px-4 py-6 md:px-6">
        <h1 className="text-2xl font-semibold text-gray-900">Invoice V2</h1>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error?.data?.message || "Unable to load this invoice."}
        </div>
      </div>
    );
  }

  async function handleSend() {
    await sendInvoiceV2({
      id: invoice._id,
      body: {
        recipients: sendRecipients
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
        subject: sendSubject,
      },
    }).unwrap();
    setSendModalVisible(false);
  }

  async function handleReminder() {
    await remindInvoiceV2({
      id: invoice._id,
      body: {
        recipients: reminderRecipients
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
        subject: reminderSubject,
      },
    }).unwrap();
    setReminderModalVisible(false);
  }

  async function handleRecordPayment() {
    await addInvoiceV2Payment({
      id: invoice._id,
      body: {
        amount: Number(paymentAmount || 0),
        reference: paymentReference,
        note: paymentNote,
      },
    }).unwrap();
    setPaymentModalVisible(false);
    setPaymentAmount("");
    setPaymentReference("");
    setPaymentNote("");
  }

  const canEdit = ["draft", "review"].includes(invoice?.status);
  const canSend = !["paid", "void"].includes(invoice?.status);
  const canRemind = ["sent", "issued", "partially_paid", "overdue"].includes(invoice?.status);

  return (
    <div className="space-y-6 px-4 py-6 md:px-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h1 className="text-2xl font-semibold text-gray-900">
              {invoice?.invoiceNumber || "Invoice V2"}
            </h1>
            <Badge
              color={getInvoiceStatusColor(invoice?.status)}
              label={getInvoiceStatusLabel(invoice?.status)}
            />
            {invoice?._id ? (
              <InvoiceTagsInline invoice={invoice} editable={canEdit} badgeSize="lg" />
            ) : null}
          </div>
          <div className="mt-2">
            <p className="text-sm text-gray-500">{invoice?.client?.name || "Unknown client"}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {canEdit ? (
            <Button onClick={() => navigate(`/invoices/v2/${invoice?._id}/edit`)}>
              Edit
            </Button>
          ) : null}
          {canSend ? <Button onClick={() => {
            setSendRecipients(invoice?.client?.email || "");
            setSendSubject(`Invoice: ${invoice?.invoiceNumber || ""}`);
            setSendModalVisible(true);
          }}>Send</Button> : null}
          {canRemind ? (
            <Button
              type="alternative"
              onClick={() => {
                setReminderRecipients(invoice?.delivery?.lastRecipients?.join(", ") || invoice?.client?.email || "");
                setReminderSubject(`Reminder: ${invoice?.invoiceNumber || ""}`);
                setReminderModalVisible(true);
              }}
            >
              Remind
            </Button>
          ) : null}
          {invoice?.status !== "void" && invoice?.totals?.balanceDue > 0 ? (
            <Button type="alternative" onClick={() => setPaymentModalVisible(true)}>
              Record payment
            </Button>
          ) : null}
          <Button type="alternative" onClick={() => navigate("/invoices/v2")}>
            Back to v2 invoices
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm md:grid-cols-2">
        <div>
          <p className="text-sm font-semibold text-gray-900">Bill to</p>
          <p className="mt-2 text-sm text-gray-700">
            {invoice?.snapshot?.billingName || invoice?.snapshot?.customerName || "N/A"}
          </p>
          <p className="text-sm text-gray-600">{invoice?.snapshot?.billingEmail || "N/A"}</p>
          <p className="mt-2 text-sm text-gray-600">
            {invoice?.snapshot?.billToAddress?.address || "N/A"}
          </p>
          <p className="text-sm text-gray-600">
            {[
              invoice?.snapshot?.billToAddress?.city,
              invoice?.snapshot?.billToAddress?.state,
              invoice?.snapshot?.billToAddress?.zipcode,
            ]
              .filter(Boolean)
              .join(", ")}
          </p>
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">Invoice details</p>
          <div className="mt-2 space-y-2 text-sm text-gray-600">
            <p>Schema version: {invoice?.schemaVersion || 1}</p>
            <p>Currency: {invoice?.currency || "USD"}</p>
            <p>Due date: {invoice?.dueDate ? String(invoice.dueDate).slice(0, 10) : "N/A"}</p>
            <p>Terms: {invoice?.terms || "N/A"}</p>
            <p>Terms preset: {invoice?.termsMeta?.label || invoice?.termsCode || "N/A"}</p>
            <p>Payment instructions: {invoice?.paymentInstructions || "N/A"}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-lg font-semibold text-gray-900">Tax defaults</h2>
          <div className="space-y-2 text-sm text-gray-600">
            <p>Mode: {invoice?.taxSettings?.mode || "line_item"}</p>
            <p>Default taxable: {invoice?.taxSettings?.defaultTaxable ? "Yes" : "No"}</p>
            <p>Default rate: {toTaxRatePercent(invoice?.taxSettings?.defaultTaxRate)}%</p>
            <p>Default code: {invoice?.taxSettings?.defaultTaxCode || "N/A"}</p>
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-lg font-semibold text-gray-900">Delivery</h2>
          <div className="space-y-2 text-sm text-gray-600">
            <p>Last sent: {invoice?.delivery?.lastSentAt ? String(invoice.delivery.lastSentAt).slice(0, 10) : "Not sent"}</p>
            <p>
              Last reminder:{" "}
              {invoice?.delivery?.lastReminderAt
                ? String(invoice.delivery.lastReminderAt).slice(0, 10)
                : "No reminders"}
            </p>
            <p>Reminder count: {invoice?.delivery?.reminderCount || 0}</p>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="mb-3 text-lg font-semibold text-gray-900">Payments</h2>
        {invoice?.paymentAllocations?.length ? (
          <div className="space-y-2">
            {invoice.paymentAllocations.map((allocation) => (
              <div
                key={allocation.paymentId}
                className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2 text-sm"
              >
                <div>
                  <p className="font-medium text-gray-900">
                    {allocation.reference || allocation.paymentId}
                  </p>
                  <p className="text-gray-500">
                    {allocation.method || "manual"} •{" "}
                    {allocation.allocatedAt
                      ? String(allocation.allocatedAt).slice(0, 10)
                      : "No date"}
                  </p>
                </div>
                <p className="font-medium text-gray-900">
                  {renderAmount(allocation.amount || 0)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No payments recorded yet.</p>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="mb-3 text-lg font-semibold text-gray-900">Line items</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 pr-4">Type</th>
                <th className="py-2 pr-4">Description</th>
                <th className="py-2 pr-4">Qty</th>
                <th className="py-2 pr-4">Unit price</th>
                <th className="py-2 pr-4">Tax</th>
                <th className="py-2 pr-4">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(invoice?.lineItems || []).map((lineItem) => (
                <tr key={lineItem.lineId}>
                  <td className="py-3 pr-4 text-gray-700">{lineItem.sourceType}</td>
                  <td className="py-3 pr-4 text-gray-900">
                    <div>{lineItem.description}</div>
                    <div className="mt-1 text-xs text-gray-500">
                      {[lineItem.category, lineItem.taxCode].filter(Boolean).join(" • ") ||
                        "No extra metadata"}
                    </div>
                  </td>
                  <td className="py-3 pr-4 text-gray-700">{lineItem.quantity}</td>
                  <td className="py-3 pr-4 text-gray-700">
                    {renderAmount(lineItem.unitPrice || 0)}
                  </td>
                  <td className="py-3 pr-4 text-gray-700">
                    {renderLineTax(lineItem)}
                  </td>
                  <td className="py-3 pr-4 font-medium text-gray-900">
                    {renderAmount(lineItem.amount || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <InvoiceSummary
        totals={invoice?.totals || {}}
        sourceRefs={invoice?.sourceRefs || []}
        discounts={invoice?.discounts || []}
        fees={invoice?.fees || []}
        credits={invoice?.credits || []}
      />

      <Modal
        visible={sendModalVisible}
        setVisible={setSendModalVisible}
        onClose={() => setSendModalVisible(false)}
        title="Send invoice"
        footer={
          <>
            <Button type="alternative" onClick={() => setSendModalVisible(false)}>
              Cancel
            </Button>
            <Button onClick={handleSend} loading={sending}>
              Send
            </Button>
          </>
        }
        content={
          <div className="space-y-4">
            <Input
              label="Recipients"
              value={sendRecipients}
              onChange={(target) => setSendRecipients(target.value)}
            />
            <Input
              label="Subject"
              value={sendSubject}
              onChange={(target) => setSendSubject(target.value)}
            />
          </div>
        }
      />

      <Modal
        visible={reminderModalVisible}
        setVisible={setReminderModalVisible}
        onClose={() => setReminderModalVisible(false)}
        title="Send reminder"
        footer={
          <>
            <Button type="alternative" onClick={() => setReminderModalVisible(false)}>
              Cancel
            </Button>
            <Button onClick={handleReminder} loading={reminding}>
              Send reminder
            </Button>
          </>
        }
        content={
          <div className="space-y-4">
            <Input
              label="Recipients"
              value={reminderRecipients}
              onChange={(target) => setReminderRecipients(target.value)}
            />
            <Input
              label="Subject"
              value={reminderSubject}
              onChange={(target) => setReminderSubject(target.value)}
            />
          </div>
        }
      />

      <Modal
        visible={paymentModalVisible}
        setVisible={setPaymentModalVisible}
        onClose={() => setPaymentModalVisible(false)}
        title="Record payment"
        footer={
          <>
            <Button type="alternative" onClick={() => setPaymentModalVisible(false)}>
              Cancel
            </Button>
            <Button onClick={handleRecordPayment} loading={recordingPayment}>
              Record payment
            </Button>
          </>
        }
        content={
          <div className="space-y-4">
            <Input
              label="Amount"
              type="number"
              value={paymentAmount}
              onChange={(target) => setPaymentAmount(target.value)}
            />
            <Input
              label="Reference"
              value={paymentReference}
              onChange={(target) => setPaymentReference(target.value)}
            />
            <TextArea
              label="Note"
              value={paymentNote}
              onChange={(event) => setPaymentNote(event.target.value)}
            />
          </div>
        }
      />
    </div>
  );
};

export default InvoiceV2Show;
